<template>
  <form @submit.prevent="requestFreeTrial">
    <h1 class="text-primary mb-4">{{ $t(`global.checkout-details`) }}</h1>
    <div class="text-secondary mb-4">{{ $t("global.free-trial-desc") }}</div>
    <FormMessage :errors="errors" v-if="errors" />
    <div class="mb-4">
      <FormInput
        name="email"
        v-model="fields.email"
        :label="`${$t('global.email')} *`"
        :errors="validation.fields.email.errors"
        :dirty="validation.fields.email.dirty"
      />
    </div>
    <div class="mb-4">
      <FormSelect
        name="classification"
        v-model="fields.classification"
        :disabled="loading"
        :label="`${$t('global.how-would-you-describe-yourself')} *`"
        :errors="validation.fields.classification.errors"
        :dirty="validation.fields.classification.dirty"
      >
        <option
          v-for="(classification, index) in classifications"
          :key="index"
          :value="classification"
        >
          {{ $t(`global.${classification}`) }}
        </option>
      </FormSelect>
    </div>

    <div class="text-right my-4">
      {{ `* ${$t("global.required")}` }}
    </div>

    <div class="my-4 text-right">
      <Btn
        type="submit"
        variant="primary"
        :disabled="loading"
        :loading="loading"
        data-test="signup-button"
      >
        {{ $t("global.request-your-free-trial") }}
      </Btn>
    </div>
    <label class="text-secondary">{{ $t("global.free-trial-terms-and-conditions") }}</label>
  </form>
</template>

<script>
import { computed, onMounted, reactive, ref } from "@vue/composition-api";
import Btn from "@/components/display/Btn";
import FormInput from "@/components/display/form/FormInput";
import FormMessage from "@/components/display/form/FormMessage";
import FormSelect from "@/components/display/form/FormSelect";
import FreeTrialApologyModal from "@/components/smart/modals/FreeTrialApology";
import { fromServer } from "@/utils/translations";
import store from "@/store";
import useAnalytics from "@/utils/useAnalytics";
import useValidator from "@/utils/useValidator";
import { freeTrialDetails as validationRules } from "leatherman-js/utils/validations/forms";

export default {
  name: "CheckoutTrialDetailsPage",
  components: {
    FormMessage,
    FormInput,
    FormSelect,
    Btn,
  },
  setup(props, { root }) {
    const loading = ref(false);
    const errors = ref(null);
    const packageId = computed(() => store.getters["checkout/getPackageId"]);
    const requireStoreBuilder = computed(() => store.getters["checkout/requireStoreBuilder"]);
    const requireStoreBuilderUI = computed(() => store.getters["checkout/requireStoreBuilderUi"]);
    const { cartData } = useAnalytics();

    const fields = reactive({
      email: store.state.checkout.email,
      locationId: store.state.checkout.locationId,
      nickname: store.state.checkout.nickname,
      classification: store.state.checkout.classification,
    });
    const { validator } = useValidator();
    const validation = validator(
      fields,
      validationRules,
      {},
      {
        email: {
          required: root.$t("validation.email-required"),
          email: root.$t("validation.invalid-email"),
        },
        nickname: {
          required: root.$t("validation.nickname-required"),
        },
        classification: {
          required: root.$t("validation.classification-required"),
        },
      }
    );

    // available classifications for the new trial user
    const classifications = ["agency", "creator", "enterprise", "merchant"];

    const requestFreeTrial = () => {
      errors.value = null;
      loading.value = true;
      validation
        .validate()
        .then(() => {
          store.dispatch("checkout/setEmail", fields.email);
          store.dispatch("checkout/setNickname", fields.nickname);
          store.dispatch("checkout/setClassification", fields.classification);

          // check if free trials are available (and if user email is valid)
          return store
            .dispatch("checkout/checkFreeTrialLimit", {
              email: fields.email,
              packageId: packageId.value,
            })
            .then(({ available, reasons }) => {
              if (available) {
                store.dispatch("analytics/send", {
                  name: "checkoutTermsAgreement",
                  data: cartData.value,
                });
                store.dispatch("analytics/send", { name: "checkoutSignup", data: cartData.value });
                store.dispatch("analytics/send", {
                  name: "checkoutPlanDetails",
                  data: cartData.value,
                });

                // if free trials are available, continue to the free trial profile step
                if (requireStoreBuilder.value) {
                  root.$router.push({
                    name: "checkout-free-trial-storebuilder",
                    query: store.getters["checkout/getQueryParams"],
                  });
                } else {
                  root.$router.push({
                    name: "checkout-free-trial-profile",
                    query: {
                      appId: store.state.checkout.appId,
                      packageId: store.state.checkout.packageId,
                      locationId: store.state.checkout.locationId,
                    },
                  });
                }
              } else {
                Object.keys(reasons).forEach((reason) => {
                  let message = reasons[reason];
                  switch (reason) {
                    // if reason is 'customer_email', show email error in form
                    case "customer_email":
                      errors.value = [root.$t("validation.invalid-email")];
                      break;
                    // if 'package_id' is the reason, show the free trial limit apology modal
                    case "package_id":
                      store.dispatch("modals/open", {
                        template: FreeTrialApologyModal,
                      });
                      break;
                    // else just show the server error
                    default:
                      errors.value = [fromServer(message)];
                      break;
                  }
                });
              }
            });
        })
        .catch(() => {
          errors.value = "Invalid email";
        })
        .finally(() => {
          loading.value = false;
        });
    };

    onMounted(() => {
      store.dispatch("locations/getList").finally(() => {
        loading.value = false;

        if (!fields.locationId) {
          // auto select the first location in the list of locations (if none is provided in the url)
          fields.locationId = store.getters["locations/getDefault"]?.id;

          store.dispatch("checkout/setLocation", fields.locationId);
        }

        store.dispatch("analytics/send", { name: "addToCart", data: cartData.value });
        store.dispatch("analytics/send", { name: "checkoutStarted", data: cartData.value });

        if (requireStoreBuilderUI.value) {
          root.$router.push({
            name: "checkout-free-trial-storebuilder",
            query: store.getters["checkout/getQueryParams"],
          });
        }
      });
    });

    return {
      loading,
      errors,
      validation,
      requestFreeTrial,
      classifications,
      fields,
    };
  },
};
</script>
